// Story 7.7 — mutation hook for POST /api/v1/admin/feed/editorial.
//
// Mirrors the Story 5.2 mutation idiom (fresh Idempotency-Key per
// submit, typed error envelope, retry: false via useMutation default).
// Unlike the marketplace hooks, THIS hook owns cache invalidation on
// success: the recent-editorial list AND the SPA's ["feed"] cache (the
// new card should appear on the next feed refresh).

import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export const ADMIN_EDITORIAL_QUERY_KEY = ["admin", "feed", "editorial"] as const;

export interface CreateEditorialPostVariables {
  title: string;
  body: string;
  media_url: string | null;
  external_url: string | null;
  tags: string[];
}

export interface CreateEditorialPostResponse {
  id: string;
  published_at: string;
  source_kind: string;
}

export interface CreateEditorialPostError {
  code: string;
  message: string;
  field?: string;
  status: number;
}

function freshIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

async function postEditorial(
  variables: CreateEditorialPostVariables,
): Promise<CreateEditorialPostResponse> {
  const response = await fetch("/api/v1/admin/feed/editorial", {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "x-idempotency-key": freshIdempotencyKey(),
    },
    body: JSON.stringify(variables),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: CreateEditorialPostError = {
      code: "EDITORIAL_POST_FAILED",
      message: `Editorial post failed (HTTP ${response.status})`,
      status: response.status,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = {
          code: raw.error.code,
          message: raw.error.message,
          field: raw.error.field,
          status: response.status,
        };
      }
    } catch {
      // Body not JSON — keep default.
    }
    throw detail;
  }
  return (await response.json()) as CreateEditorialPostResponse;
}

export function useCreateEditorialPost(): UseMutationResult<
  CreateEditorialPostResponse,
  CreateEditorialPostError,
  CreateEditorialPostVariables
> {
  const queryClient = useQueryClient();
  return useMutation<
    CreateEditorialPostResponse,
    CreateEditorialPostError,
    CreateEditorialPostVariables
  >({
    mutationFn: postEditorial,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ADMIN_EDITORIAL_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: ["feed"] });
    },
  });
}
